import { useState } from "react";
import { Sparkles, RefreshCw, AlertTriangle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useSubscription } from "../hooks/useSubscription";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { logError } from "../lib/logError";

interface ClaimsReportData {
  period: string;
  total_inc_gst: number;
  paid_by_builder: number;
  calculated_payment_amount: number;
  difference: number;
  days_before_due: number | null;
  date_paid: string | null;
  submission_date: string | null;
  payment_due_date: string | null;
}

interface ClaimsInsightProps {
  projectName: string;
  reports: ClaimsReportData[];
  retentionCeiling: number;
  accumulatedRetention: number;
}

export function ClaimsInsight({
  projectName,
  reports,
  retentionCeiling,
  accumulatedRetention,
}: ClaimsInsightProps) {
  const [insight, setInsight] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { logAiUsage, canUseAi } = useSubscription();

  const generateInsight = async () => {
    // Pre-flight credit check
    const check = canUseAi("claims_insight");
    if (!check.allowed) {
      setError(check.reason || "AI usage not available");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        projectName,
        retentionCeiling,
        accumulatedRetention,
        reports: reports.map((r) => ({
          period: r.period,
          invoiced: r.total_inc_gst,
          calculatedPayment: r.calculated_payment_amount,
          paid: r.paid_by_builder,
          difference: r.difference,
          daysBeforeDue: r.days_before_due,
          datePaid: r.date_paid,
          submitted: r.submission_date,
          due: r.payment_due_date,
        })),
      };

      const { data, error: fnError } = await supabase.functions.invoke(
        "generate-claims-insight",
        { body: payload },
      );

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setInsight(data.analysis);

      // Log AI credit usage with token data from edge function
      if (data.usage) {
        logAiUsage("claims_insight", data.usage);
      } else {
        logAiUsage("claims_insight");
      }
    } catch (err) {
      logError("Failed to generate claims insight:", err);
      if (err instanceof TypeError && err.message === "Failed to fetch") {
        setError("Connection error. Please check your network.");
      } else {
        setError(
          err instanceof Error ? err.message : "Unable to generate insight.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial "call to action" state
  if (!insight && !loading && !error) {
    return (
      <div
        className="card"
        style={{
          background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
          border: "1px solid #c4b5fd",
          padding: "18px 22px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#7c3aed",
              }}
            >
              <Sparkles size={18} />
            </div>
            <div>
              <h3
                style={{
                  fontSize: "0.92rem",
                  fontWeight: 700,
                  color: "#5b21b6",
                  marginBottom: 1,
                }}
              >
                AI Claims Analyst
              </h3>
              <p style={{ fontSize: "0.76rem", color: "#6d28d9" }}>
                Analyze payment trends, overdue risk, and builder payment
                behavior.
              </p>
            </div>
          </div>
          <button
            onClick={generateInsight}
            className="btn"
            disabled={reports.length === 0}
            style={{
              background: "#7c3aed",
              color: "white",
              border: "none",
              boxShadow: "0 2px 4px rgba(124,58,237,0.2)",
              fontSize: "0.82rem",
              opacity: reports.length === 0 ? 0.5 : 1,
            }}
          >
            <Sparkles size={13} style={{ marginRight: 4, display: "inline" }} />
            Analyze
          </button>
        </div>
      </div>
    );
  }

  // Results / loading / error state
  return (
    <div
      className="card"
      style={{ borderTop: "4px solid #8b5cf6", padding: "18px 22px" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={17} style={{ color: "#8b5cf6" }} />
          <h3
            style={{
              fontSize: "0.92rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            AI Claims Analysis
          </h3>
        </div>
        <button
          onClick={generateInsight}
          disabled={loading}
          className="btn btn-ghost btn-sm"
          style={{
            color: "#6b7280",
            fontSize: "0.78rem",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <RefreshCw size={14} className={loading ? "spin" : ""} />
          {loading ? "Analyzing..." : "Refresh"}
        </button>
      </div>

      <div style={{ lineHeight: 1.65, fontSize: "0.85rem", color: "#374151" }}>
        {error ? (
          <div
            style={{
              display: "flex",
              gap: 8,
              color: "#dc2626",
              alignItems: "center",
              padding: "12px",
              background: "#fef2f2",
              borderRadius: 8,
            }}
          >
            <AlertTriangle size={18} />
            {error}
          </div>
        ) : loading ? (
          <div
            style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}
          >
            <div className="spinner" style={{ margin: "0 auto 12px" }} />
            Analyzing {reports.length} claim records...
          </div>
        ) : (
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                 
                table: ({ node, ...props }) => (
                  <div style={{ overflowX: "auto", margin: "0.8rem 0" }}>
                    <table
                      style={{
                        borderCollapse: "collapse",
                        width: "100%",
                        fontSize: "0.82rem",
                      }}
                      {...props}
                    />
                  </div>
                ),
                 
                th: ({ node, ...props }) => (
                  <th
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "7px 10px",
                      background: "#f9fafb",
                      fontWeight: 600,
                      textAlign: "left",
                      whiteSpace: "nowrap",
                    }}
                    {...props}
                  />
                ),
                 
                td: ({ node, ...props }) => (
                  <td
                    style={{ border: "1px solid #e5e7eb", padding: "7px 10px" }}
                    {...props}
                  />
                ),
              }}
            >
              {insight || ""}
            </ReactMarkdown>
            <div
              style={{
                marginTop: 12,
                borderTop: "1px solid #e5e7eb",
                paddingTop: 8,
                fontSize: "0.68rem",
                color: "#9ca3af",
                fontStyle: "italic",
              }}
            >
              AI-generated analysis. Verify figures with project records.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
